import React, { useEffect, useMemo, useState } from "react";
import { api } from "../lib/api";
import {
  Plus,
  Trash2,
  Copy,
  Link2,
  GripVertical,
  MessageSquare,
  Rows3,
  GanttChartSquare,
  ExternalLink,
  ClipboardCopy,
} from "lucide-react";
import { toast } from "sonner";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "./ui/select";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "./ui/dialog";
import { Input } from "./ui/input";
import { Textarea } from "./ui/textarea";
import { Button } from "./ui/button";
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
} from "@dnd-kit/core";
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { AgentTimelineView, TimelineDialog, DateRangeCell, RemarkCell } from "./AgentTaskTable";
import { copyTableToClipboard } from "../lib/copyTable";

const fmtShortDate = (iso) => {
  if (!/^\d{4}-\d{2}-\d{2}$/.test(iso || "")) return "";
  const [, m, d] = iso.split("-").map(Number);
  return `${d}/${m}`;
};
const rowDateLabel = (row) => {
  const p = (row.timeline && row.timeline[0]) || null;
  const s = fmtShortDate(p?.start_date);
  const e = fmtShortDate(p?.end_date);
  if (!s && !e) return "";
  return e && e !== s ? `${s} – ${e}` : s || e;
};
const rowRemark = (row) => ((row.timeline && row.timeline[0]) || {}).task || "";

const GRID = "28px 1.4fr 1.4fr 140px 140px 150px minmax(150px,1fr) 140px 110px";

export const PRODUCT_TASK_STATUSES = ["Assigned", "WIP", "Upgrade", "Completed"];

const PT_STATUS_META = {
  "Assigned":  { bg: "bg-slate-100",  text: "text-slate-600" },
  "WIP":       { bg: "bg-sky-100",    text: "text-sky-700" },
  "Upgrade":   { bg: "bg-butter-100", text: "text-amber-700" },
  "Completed": { bg: "bg-mint-100",   text: "text-emerald-700" },
};

// Auto-growing textarea so long Task / Sub-task text wraps and is fully visible
// (looks like an input but expands to multiple lines instead of truncating).
function WrapTextarea({ value, onCommit, placeholder, testid }) {
  const ref = React.useRef(null);
  const resize = () => {
    const el = ref.current;
    if (!el) return;
    el.style.height = "auto";
    el.style.height = `${el.scrollHeight}px`;
  };
  React.useEffect(() => { resize(); }, [value]);
  return (
    <textarea
      ref={ref}
      key={value}
      defaultValue={value || ""}
      rows={1}
      onInput={resize}
      onBlur={(e) => { const v = e.target.value.trim(); if (v !== (value || "")) onCommit(v); }}
      onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); e.currentTarget.blur(); } }}
      placeholder={placeholder}
      data-testid={testid}
      className="block w-full min-w-0 resize-none rounded-md border border-slate-200 bg-white text-sm text-slate-700 leading-snug py-1.5 px-2 outline-none focus:border-blue-400 focus:ring-1 focus:ring-blue-200 placeholder:text-slate-400 whitespace-pre-wrap break-words"
    />
  );
}

// --- Sortable row ---
function ProductTaskRow({ r, members, onUpdate, onDuplicate, onDelete, onOpenRefs, refsCount }) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id: r.id });
  const style = { transform: CSS.Transform.toString(transform), transition, opacity: isDragging ? 0.5 : 1, gridTemplateColumns: GRID };

  return (
    <div
      ref={setNodeRef}
      style={style}
      className="grid gap-2 px-4 min-h-[48px] py-2 items-start border-b border-slate-100 hover:bg-slate-50/40 group"
      data-testid={`pt-row-${r.id}`}
    >
      <div className="flex justify-center pt-1.5">
        <button
          {...attributes}
          {...listeners}
          className="opacity-0 group-hover:opacity-100 cursor-grab text-slate-400 hover:text-slate-700"
          data-testid={`pt-drag-${r.id}`}
        >
          <GripVertical className="h-4 w-4" />
        </button>
      </div>
      <WrapTextarea
        value={r.task}
        onCommit={(v) => { if (v !== r.task) onUpdate(r.id, { task: v }); }}
        placeholder="Task"
        testid={`pt-task-${r.id}`}
      />
      <WrapTextarea
        value={r.sub_task}
        onCommit={(v) => { if (v !== r.sub_task) onUpdate(r.id, { sub_task: v }); }}
        placeholder="Sub-task"
        testid={`pt-subtask-${r.id}`}
      />
      <Select
        value={r.workflow_integrator || "__none__"}
        onValueChange={(v) => onUpdate(r.id, { workflow_integrator: v === "__none__" ? "" : v })}
      >
        <SelectTrigger className="h-8 text-xs" data-testid={`pt-workflow-${r.id}`}>
          <SelectValue placeholder="Workflow" />
        </SelectTrigger>
        <SelectContent>
          <SelectItem value="__none__"><span className="text-slate-500">Unassigned</span></SelectItem>
          {members.map((m) => <SelectItem key={m.id} value={m.email}>{m.name}</SelectItem>)}
        </SelectContent>
      </Select>
      <Select
        value={r.tester_adopter || "__none__"}
        onValueChange={(v) => onUpdate(r.id, { tester_adopter: v === "__none__" ? "" : v })}
      >
        <SelectTrigger className="h-8 text-xs" data-testid={`pt-tester-${r.id}`}>
          <SelectValue placeholder="Tester" />
        </SelectTrigger>
        <SelectContent>
          <SelectItem value="__none__"><span className="text-slate-500">Unassigned</span></SelectItem>
          {members.map((m) => <SelectItem key={m.id} value={m.email}>{m.name}</SelectItem>)}
        </SelectContent>
      </Select>
      <div>
        <DateRangeCell row={r} onUpdate={onUpdate} testid={`pt-date-${r.id}`} />
      </div>
      <div>
        <RemarkCell row={r} onUpdate={onUpdate} testid={`pt-remark-${r.id}`} />
      </div>
      <Select value={r.status || "Assigned"} onValueChange={(v) => onUpdate(r.id, { status: v })}>
        <SelectTrigger className={`h-8 text-xs border-0 font-medium ${(PT_STATUS_META[r.status] || PT_STATUS_META["Assigned"]).bg} ${(PT_STATUS_META[r.status] || PT_STATUS_META["Assigned"]).text}`} data-testid={`pt-status-${r.id}`}>
          <SelectValue />
        </SelectTrigger>
        <SelectContent>
          {PRODUCT_TASK_STATUSES.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
        </SelectContent>
      </Select>
      <div className="flex items-center justify-end gap-1">
        <button onClick={() => onOpenRefs(r)} className="relative h-8 w-8 rounded-md hover:bg-sky-50 text-slate-500 hover:text-blue-600 flex items-center justify-center" title="References" data-testid={`pt-refs-${r.id}`}>
          <Link2 className="h-4 w-4" />
          {refsCount > 0 && <span className="absolute -top-1 -right-1 h-4 min-w-[16px] px-1 rounded-full bg-blue-600 text-white text-[10px] font-bold flex items-center justify-center">{refsCount}</span>}
        </button>
        <button onClick={() => onDuplicate(r)} className="h-8 w-8 rounded-md hover:bg-slate-100 text-slate-500 hover:text-slate-800 flex items-center justify-center" title="Duplicate" data-testid={`pt-duplicate-${r.id}`}>
          <Copy className="h-4 w-4" />
        </button>
        <button onClick={() => onDelete(r)} className="h-8 w-8 rounded-md hover:bg-rose-50 text-slate-400 hover:text-rose-600 flex items-center justify-center" title="Delete" data-testid={`pt-delete-${r.id}`}>
          <Trash2 className="h-4 w-4" />
        </button>
      </div>
    </div>
  );
}

// --- References dialog (per product-task) ---
function PtReferencesDialog({ open, onOpenChange, row, onSave }) {
  const [items, setItems] = useState([]);
  useEffect(() => { if (open && row) setItems((row.references || []).map((r) => ({ ...r }))); }, [open, row]);
  if (!row) return null;
  const add = () => setItems((p) => [...p, { id: `tmp-${Date.now()}-${Math.random().toString(36).slice(-4)}`, url: "", description: "", status: "" }]);
  const update = (id, k, v) => setItems((p) => p.map((it) => (it.id === id ? { ...it, [k]: v } : it)));
  const remove = (id) => setItems((p) => p.filter((it) => it.id !== id));
  const submit = async () => {
    await onSave({ references: items.filter((r) => r.url || r.description) });
    onOpenChange(false);
    toast.success("References saved");
  };
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-2xl" data-testid="pt-refs-dialog">
        <DialogHeader>
          <DialogTitle className="font-display flex items-center gap-2"><Link2 className="h-5 w-5 text-blue-600" /> References</DialogTitle>
          <DialogDescription>Add reference links and descriptions for this task.</DialogDescription>
        </DialogHeader>
        <div className="space-y-2 max-h-[420px] overflow-y-auto">
          {items.length === 0 ? (
            <p className="text-xs text-slate-400 text-center py-6">No references yet.</p>
          ) : items.map((it) => (
            <div key={it.id} className="flex items-center gap-2 group">
              <Input value={it.url} onChange={(e) => update(it.id, "url", e.target.value)} placeholder="https://…" className="h-9 text-sm flex-1" />
              <Input value={it.description} onChange={(e) => update(it.id, "description", e.target.value)} placeholder="Description" className="h-9 text-sm flex-1" />
              {it.url && (
                <a href={it.url} target="_blank" rel="noreferrer" className="h-9 w-9 rounded-md hover:bg-slate-100 flex items-center justify-center text-slate-500 hover:text-blue-600">
                  <ExternalLink className="h-4 w-4" />
                </a>
              )}
              <button onClick={() => remove(it.id)} className="opacity-0 group-hover:opacity-100 h-9 w-9 rounded-md hover:bg-rose-50 text-slate-400 hover:text-rose-600 flex items-center justify-center">
                <Trash2 className="h-4 w-4" />
              </button>
            </div>
          ))}
        </div>
        <Button variant="outline" size="sm" onClick={add} className="w-fit"><Plus className="h-3.5 w-3.5 mr-1" /> Add reference</Button>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button onClick={submit} className="btn-brand" data-testid="pt-refs-save">Save</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// --- Remarks dialog (per product-task, date-wise) ---
function PtRemarksDialog({ open, onOpenChange, row, onSave }) {
  const [items, setItems] = useState([]);
  useEffect(() => { if (open && row) setItems((row.remarks_log || []).map((r) => ({ ...r }))); }, [open, row]);
  if (!row) return null;
  const todayISO = new Date().toISOString().slice(0, 10);
  const add = () => setItems((p) => [...p, { id: `tmp-${Date.now()}-${Math.random().toString(36).slice(-4)}`, date: todayISO, text: "" }]);
  const update = (id, k, v) => setItems((p) => p.map((it) => (it.id === id ? { ...it, [k]: v } : it)));
  const remove = (id) => setItems((p) => p.filter((it) => it.id !== id));
  const submit = async () => {
    await onSave({ remarks_log: items.filter((r) => r.text && r.text.trim()) });
    onOpenChange(false);
    toast.success("Remarks saved");
  };
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-2xl" data-testid="pt-remarks-dialog">
        <DialogHeader>
          <DialogTitle className="font-display flex items-center gap-2"><MessageSquare className="h-5 w-5 text-amber-600" /> Remarks</DialogTitle>
          <DialogDescription>Date-stamped notes for this task.</DialogDescription>
        </DialogHeader>
        <div className="space-y-2 max-h-[420px] overflow-y-auto">
          {items.length === 0 ? (
            <p className="text-xs text-slate-400 text-center py-6">No remarks yet.</p>
          ) : items.map((it) => (
            <div key={it.id} className="flex items-start gap-2 group">
              <Input type="date" value={it.date} onChange={(e) => update(it.id, "date", e.target.value)} className="h-9 text-xs w-[140px]" />
              <Textarea value={it.text} onChange={(e) => update(it.id, "text", e.target.value)} placeholder="Remark" className="text-sm flex-1 min-h-[64px]" />
              <button onClick={() => remove(it.id)} className="opacity-0 group-hover:opacity-100 h-9 w-9 rounded-md hover:bg-rose-50 text-slate-400 hover:text-rose-600 flex items-center justify-center">
                <Trash2 className="h-4 w-4" />
              </button>
            </div>
          ))}
        </div>
        <Button variant="outline" size="sm" onClick={add} className="w-fit"><Plus className="h-3.5 w-3.5 mr-1" /> Add remark</Button>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button onClick={submit} className="btn-brand" data-testid="pt-remarks-save">Save</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// --- Main component ---
export default function ProductTaskTable({ members, agents = [], kind = "product" }) {
  const [rows, setRows] = useState([]);
  const [allProductRows, setAllProductRows] = useState([]); // all kinds — used for combined timeline
  const [agentRows, setAgentRows] = useState([]); // for combined timeline
  const [refsOpen, setRefsOpen] = useState(false);
  const [remarksOpen, setRemarksOpen] = useState(false);
  const [timelineOpen, setTimelineOpen] = useState(false);
  const [activeRow, setActiveRow] = useState(null);
  const [personFilter, setPersonFilter] = useState("__all__");
  const [viewMode, setViewMode] = useState("list");

  const isBug = kind === "bug";
  const isSecurity = kind === "security";
  const labels = {
    title: isBug ? "Bugs" : isSecurity ? "Security" : "Product",
    subtitle: isBug
      ? "A flat list of bugs with workflow + tester assignments. Timeline view aggregates entries from Product, Security, Bugs and Agent."
      : isSecurity
      ? "A flat list of security tasks with workflow + tester assignments. Timeline view aggregates entries from Product, Security, Bugs and Agent."
      : "A flat list of product tasks with workflow + tester assignments. Timeline view aggregates entries from Product, Security, Bugs and Agent.",
    addLabel: isBug ? "Add bug" : isSecurity ? "Add security task" : "Add row",
    emptyMsg: isBug
      ? "No bugs yet — click \"Add bug\" to log one."
      : isSecurity
      ? "No security tasks yet — click \"Add security task\" to create one."
      : "No tasks yet — click \"Add row\" to create one.",
  };

  const dragSensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 5 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
  );

  const handleDragEnd = (event) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;
    setRows((prev) => {
      const oldIdx = prev.findIndex((r) => r.id === active.id);
      const newIdx = prev.findIndex((r) => r.id === over.id);
      if (oldIdx === -1 || newIdx === -1) return prev;
      const next = arrayMove(prev, oldIdx, newIdx);
      api.post("/product-tasks/reorder", { order: next.map((r) => r.id) }).catch(() => toast.error("Reorder failed"));
      return next;
    });
  };

  const load = async () => {
    try {
      const { data } = await api.get(`/product-tasks?kind=${encodeURIComponent(kind)}`);
      setRows(data);
    } catch (e) { console.error(e); }
  };
  const loadAllProduct = async () => {
    try {
      const { data } = await api.get("/product-tasks");
      setAllProductRows(data);
    } catch (e) { console.error(e); }
  };
  const loadAgentRows = async () => {
    try {
      const { data } = await api.get("/agent-tasks");
      setAgentRows(data);
    } catch (e) { console.error(e); }
  };
  useEffect(() => { load(); loadAllProduct(); loadAgentRows(); }, [kind]);

  const handleCreate = async () => {
    try {
      const { data } = await api.post("/product-tasks", { kind });
      setRows((prev) => [...prev, data]);
      setAllProductRows((prev) => [...prev, data]);
    } catch { toast.error("Failed to add row"); }
  };
  const handleUpdate = async (id, patch) => {
    setRows((prev) => prev.map((r) => (r.id === id ? { ...r, ...patch } : r)));
    setAllProductRows((prev) => prev.map((r) => (r.id === id ? { ...r, ...patch } : r)));
    try {
      const { data } = await api.patch(`/product-tasks/${id}`, patch);
      setRows((prev) => prev.map((r) => (r.id === id ? data : r)));
      setAllProductRows((prev) => prev.map((r) => (r.id === id ? data : r)));
      return data;
    } catch { toast.error("Update failed"); load(); loadAllProduct(); }
  };
  const handleDelete = async (r) => {
    if (!window.confirm(`Delete "${r.task || "this task"}"?`)) return;
    try {
      await api.delete(`/product-tasks/${r.id}`);
      setRows((prev) => prev.filter((x) => x.id !== r.id));
      setAllProductRows((prev) => prev.filter((x) => x.id !== r.id));
      toast.success("Deleted");
    } catch { toast.error("Delete failed"); }
  };
  const handleDuplicate = async (r) => {
    try {
      await api.post(`/product-tasks/${r.id}/duplicate`);
      await load();
      await loadAllProduct();
      toast.success("Duplicated");
    } catch { toast.error("Duplicate failed"); }
  };

  const memberName = (email) => members.find((m) => m.email === email)?.name || email || "";

  const handleCopyTable = async () => {
    if (rows.length === 0) { toast.error("Nothing to copy yet"); return; }
    const headers = ["Task", "Sub-task", "Workflow", "Tester", "Date", "Remarks", "Status"];
    const data = rows.map((r) => [
      r.task || "",
      r.sub_task || "",
      memberName(r.workflow_integrator),
      memberName(r.tester_adopter),
      rowDateLabel(r),
      rowRemark(r),
      r.status || "Assigned",
    ]);
    const ok = await copyTableToClipboard({ headers, rows: data });
    if (ok) toast.success(`Copied ${rows.length} ${rows.length === 1 ? "row" : "rows"} — paste into Docs, email or Sheets`);
    else toast.error("Copy failed — your browser blocked clipboard access");
  };

  return (
    <div className="space-y-4" data-testid={`product-task-table-${kind}`}>
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div>
          <h3 className="font-display text-lg font-semibold">{labels.title}</h3>
          <p className="text-xs text-slate-500">{labels.subtitle}</p>
        </div>
        <div className="flex items-center gap-2">
          <div className="flex items-center rounded-xl bg-white border border-slate-200 p-1" data-testid="pt-view-toggle">
            <button
              onClick={() => setViewMode("list")}
              className={`h-8 px-3 rounded-lg text-xs font-medium flex items-center gap-1.5 transition-colors ${viewMode === "list" ? "btn-brand" : "text-slate-600 hover:bg-slate-50"}`}
              data-testid="pt-view-list"
            >
              <Rows3 className="h-3.5 w-3.5" /> List
            </button>
            <button
              onClick={() => setViewMode("timeline")}
              className={`h-8 px-3 rounded-lg text-xs font-medium flex items-center gap-1.5 transition-colors ${viewMode === "timeline" ? "btn-brand" : "text-slate-600 hover:bg-slate-50"}`}
              data-testid="pt-view-timeline"
            >
              <GanttChartSquare className="h-3.5 w-3.5" /> Timeline
            </button>
          </div>
          <Select value={personFilter} onValueChange={setPersonFilter}>
            <SelectTrigger className="h-10 w-56 rounded-xl text-sm" data-testid="pt-person-filter">
              <SelectValue placeholder="Filter by person" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="__all__"><span className="text-slate-700">All people</span></SelectItem>
              <SelectItem value="__unassigned__"><span className="text-slate-500">Unassigned only</span></SelectItem>
              {members.map((m) => (
                <SelectItem key={m.id} value={m.email}>{m.name}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          {viewMode === "list" && (
            <>
              <Button
                onClick={handleCopyTable}
                variant="outline"
                className="h-10 rounded-xl border-slate-200 text-slate-700 hover:bg-slate-50"
                data-testid="pt-copy-table"
                title="Copy the whole list as a table (paste into Docs, email or Sheets)"
              >
                <ClipboardCopy className="h-4 w-4 mr-1" /> Copy
              </Button>
              <Button onClick={handleCreate} className="btn-brand h-10 rounded-xl" data-testid="pt-new-row">
                <Plus className="h-4 w-4 mr-1" /> {labels.addLabel}
              </Button>
            </>
          )}
        </div>
      </div>

      {personFilter !== "__all__" && (
        <div className="text-xs text-slate-500 flex items-center gap-2">
          <span>Showing only rows where</span>
          <span className="px-2 py-0.5 rounded-md bg-blue-50 text-blue-700 font-medium">
            {personFilter === "__unassigned__"
              ? "Workflow or Tester is unassigned"
              : `${members.find((m) => m.email === personFilter)?.name || personFilter} is the Workflow or Tester`}
          </span>
          <button onClick={() => setPersonFilter("__all__")} className="text-blue-600 hover:underline" data-testid="pt-person-filter-clear">Clear</button>
        </div>
      )}

      {viewMode === "timeline" ? (
        <AgentTimelineView
          rows={agentRows}
          productRows={allProductRows}
          agents={agents}
          members={members}
          personFilter={personFilter}
          onCommit={async (entry, patch) => {
            const nextTimeline = (entry.row_timeline || []).map((t) =>
              t.id === entry.id ? { ...t, ...patch } : t
            );
            if (entry.source === "product") {
              setAllProductRows((prev) => prev.map((r) => (r.id === entry.task_id ? { ...r, timeline: nextTimeline } : r)));
              setRows((prev) => prev.map((r) => (r.id === entry.task_id ? { ...r, timeline: nextTimeline } : r)));
              try {
                const { data } = await api.patch(`/product-tasks/${entry.task_id}`, { timeline: nextTimeline });
                setAllProductRows((prev) => prev.map((r) => (r.id === entry.task_id ? data : r)));
                setRows((prev) => prev.map((r) => (r.id === entry.task_id ? data : r)));
              } catch { toast.error("Update failed"); load(); loadAllProduct(); }
            } else {
              setAgentRows((prev) => prev.map((r) => (r.id === entry.task_id ? { ...r, timeline: nextTimeline } : r)));
              try {
                const { data } = await api.patch(`/agent-tasks/${entry.task_id}`, { timeline: nextTimeline });
                setAgentRows((prev) => prev.map((r) => (r.id === entry.task_id ? data : r)));
              } catch { toast.error("Update failed"); loadAgentRows(); }
            }
          }}
        />
      ) : (
        <div className="bg-white rounded-2xl border border-slate-200 overflow-x-auto">
          <div style={{ minWidth: 1240 }}>
            <div
              className="grid gap-2 text-[11px] font-bold uppercase tracking-[0.12em] text-slate-700 px-4 h-11 items-center bg-slate-100/70 border-b border-slate-200 whitespace-nowrap"
              style={{ gridTemplateColumns: GRID }}
            >
              <div></div>
              <div>Task</div>
              <div>Sub-task</div>
              <div>Workflow</div>
              <div>Tester</div>
              <div>Date</div>
              <div>Remarks</div>
              <div>Status</div>
              <div className="text-right pr-2">Actions</div>
            </div>
            {rows.length === 0 ? (
              <div className="px-4 py-12 text-center text-sm text-slate-400">
                {labels.emptyMsg}
              </div>
            ) : (
              <DndContext sensors={dragSensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
                <SortableContext items={rows.map((r) => r.id)} strategy={verticalListSortingStrategy}>
                  {rows.map((r) => (
                    <ProductTaskRow
                      key={r.id}
                      r={r}
                      members={members}
                      onUpdate={handleUpdate}
                      onDuplicate={handleDuplicate}
                      onDelete={handleDelete}
                      onOpenRefs={(row) => { setActiveRow(row); setRefsOpen(true); }}
                      refsCount={(r.references || []).length}
                    />
                  ))}
                </SortableContext>
              </DndContext>
            )}
          </div>
        </div>
      )}

      <PtReferencesDialog
        open={refsOpen}
        onOpenChange={setRefsOpen}
        row={activeRow}
        onSave={async (patch) => {
          if (!activeRow) return;
          await handleUpdate(activeRow.id, patch);
        }}
      />
      <PtRemarksDialog
        open={remarksOpen}
        onOpenChange={setRemarksOpen}
        row={activeRow}
        onSave={async (patch) => {
          if (!activeRow) return;
          await handleUpdate(activeRow.id, patch);
        }}
      />
      <TimelineDialog
        open={timelineOpen}
        onOpenChange={setTimelineOpen}
        agentTask={activeRow}
        members={members}
        onSave={async (patch) => {
          if (!activeRow) return;
          await handleUpdate(activeRow.id, patch);
          // After saving a product timeline change, refresh agent rows so any cross-tab sync stays current
          loadAgentRows();
        }}
      />
    </div>
  );
}
